// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';
import { abbreviateText } from '@automator/shared/utils/abbreviate-text';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x, sprintf } from '@wordpress/i18n';

// Import apiFetch for the REST remote-data path
import apiFetch from '@wordpress/api-fetch';

// Import html-entities
import { decode } from 'html-entities';

// Import definitions
import { getIntegration } from '@automator/builder/definitions/integrations';

// eslint-disable-next-line import/no-unresolved
import $ from 'jquery';

import flagItemsWithVisibleID from '@automator/builder/_legacy/js/item/utilities/flag-items-with-visible-id';

import Field from '../field';
import FieldsRepeater from '../field/repeater.js';
import sanitizeFieldAttributes from '@automator/builder/_legacy/js/field/utilities/sanitize-attributes';

import RecipeTypeSelector from '../recipe/type-selector';

import { WalkthroughCreateRecipe } from '../walkthrough';

import Tokens from '../tokens';

import Modal from './modal.js';

import {
	flagActionsScheduled
} from '@automator/builder/_legacy/js/item/schedule/utilities';

import {
	flagActionsSortable
} from '@automator/builder/_legacy/js/item/sortability/utilities';

import {
	parseConditionsInMeta,
	addConditionsToIntegrations,
	flagActionsWithConditions
} from '@automator/builder/_legacy/js/item/conditions/utilities';

/**
 * Creates the data required for Automator to render
 *
 * @param {Function} callback The function to invoke after the data is ready
 * @return {undefined}
 */
export const createDataOnLoad = ( callback = '' ) => {
	// Check if there is a callback defined
	// eslint-disable-next-line no-empty-function
	callback = ! isEmpty( callback ) ? callback : () => {};

	/**
	 * Completes the process of UI creation once it knows what's the
	 * recipe type the user wants to create
	 *
	 * @return {undefined}
	 */
	const completeProcess = () => {
		// Filter Automator items
		filterAutomatorItemsByRecipeType();
		// Group the triggers and actions by integration
		groupItemsByIntegration();

		// Get recipe items on page load
		__deprecated__recipeObject( 'create', {
			allRecipes: UncannyAutomator.recipes_object,
		} );

		// Remove unnecessary data
		// delete UncannyAutomator.recipes_object;
		delete UncannyAutomator.triggers;
		delete UncannyAutomator.actions;
		delete UncannyAutomator.actionsConditions;

		// Invoke the callback
		callback();
	};

	// Check if WP is ready
	if ( ! isEmpty( UncannyAutomator._recipe.recipe_type ) ) {
		// Complete the process
		completeProcess();
	} else {
		// Create the walkthrough
		new WalkthroughCreateRecipe();
		
		// Call to Recipe Type Selector
		new RecipeTypeSelector( () => {
			// Complete the process
			completeProcess();
		} );
	}
};

/**
 * Updates all the objects used for the UI using the recipes_object object
 *
 * @param {String} action     Action to perform. Valid actions: 'create', 'delete',
 * @param {Object} attributes Data, check each switch to see the required data
 * @return {Boolean}            True if the action was made successfully
 */
export const __deprecated__recipeObject = ( action = null, attributes = {} ) => {
	// Create response variable and set default value
	let response = false;

	// Check if the action is not undefined and if it's a valid action
	if ( isDefined( action ) ) {
		switch ( action ) {
			case 'create':
				// Get this recipe in the allRecipes object
				let recipe;

				// Create allRecipes variable
				const allRecipes = attributes.allRecipes;

				$.each( allRecipes, ( index, theRecipe ) => {
					if ( theRecipe.ID == UncannyAutomator._recipe.recipe_id ) {
						recipe = theRecipe;
					}
				} );

				// Store extra options in the main UncannyAutomator object for future use
				if ( isDefined( recipe.extra_options ) && ! isEmpty( recipe.extra_options ) ) {
					UncannyAutomator.recipes_object[ recipe.ID ].extra_options = recipe.extra_options;
				}

				// Add element to all triggers, actions and closures to define the itemType. We're going to need this later
				$.each( recipe.triggers, ( index, item ) => ( item.itemType = 'trigger' ) );
				$.each( recipe.actions, ( index, item ) => ( item.itemType = 'action' ) );
				$.each( recipe.closures, ( index, item ) => ( item.itemType = 'closure' ) );

				// Merge all the recipe triggers, actions and closures into one array
				const items = [].concat.apply( [], [ recipe.triggers, recipe.actions, recipe.closures ] );

				// Reset items object
				itemsObject( 'reset' );

				// Check if the recipe is anonymous
				const recipeIsAnonymous = UncannyAutomator._recipe.recipe_type == 'anonymous';

				// Iterate each item and add it to the itemsObject
				$.each( items, ( index, item ) => {
					// We're going to get all the required information to before adding the item to the items object
					let itemToAdd;

					// Get the integration ID
					const itemIntegration = item.meta.integration;

					// Get the item code in this variable
					const itemCode = item.meta.code;

					let extraOptions;

					try {
						extraOptions =
							UncannyAutomator.recipes_object[ recipe.ID ].extra_options[ itemIntegration ][
								itemCode
							];
					} catch ( error ) {
						// There are no extra options for this item, do nothing
					}

					// Get the data from the global item
					const globalItem = getGlobalItem( itemIntegration, item.itemType, itemCode );

					// Get the integration data
					const integration = getIntegration( itemIntegration );

					// Check if the item is missing. First, check if it's an app
					const isMissing = integration?.is_app 
						// If it's an app, check if it's connected. If it's not, then it's missing
						? ! integration.miscellaneous.is_app_connected
						// If it's not an app, check if the global item exists. If it does, then it's not missing
						: ! isDefined( globalItem );

					// Check if the required PHP code for this item exists
					if ( ! isMissing ) {
						// Create variable to save all the options
						const options = {};

						if ( ! isEmpty( extraOptions ) ) {
							// If extra options were supplied, they will be used instead of the gloab ones
							if ( isDefined( extraOptions.options ) ) {
								$.each( extraOptions.options, ( i, option ) => {
									options[ option.option_code ] = [ option ];
								} );
							}

							if ( isDefined( extraOptions.options_group ) ) {
								$.extend( true, options, extraOptions.options_group );
							}
						} else {
							// Check if it has simple options (options that aren't part of a group of options)
							if ( isDefined( globalItem.options ) ) {
								$.each( globalItem.options, ( i, option ) => {
									options[ option.option_code ] = [ option ];
								} );
							}

							// Add group of options
							if ( isDefined( globalItem.options_group ) ) {
								$.extend( true, options, globalItem.options_group );
							}
						}

						// We're going to create another options variable to create the options object as we're going to add it to the item object
						const groupsOfOptions = {};

						$.each( options, ( groupKey, fields ) => {
							const allFieldsOnThisGroup = [];

							// Check if it has more this group of fields has more than one option
							const hasMoreThanOneOption = fields.length > 1;

							$.each( fields, ( i, field ) => {
								// Set the item type
								field.itemType = item.itemType;

								/**
								 * Sanitizes the field data
								 *
								 * @param {Object} field The field data
								 * @return {Object}      The field data, sanitized
								 */
								// eslint-disable-next-line require-jsdoc, no-shadow
								const sanitizeFieldData = ( field ) => { 
									// Create field object
									const fieldObject = Object.assign( sanitizeFieldAttributes( field ), {
										isGroup: hasMoreThanOneOption,
										itemId: item.ID,

										groupId: groupKey,

										currentValue: isDefined( item.meta[ field.option_code ] )
											? item.meta[ field.option_code ]
											: field.default_value,
									} );

									// If it's a select, check if it support custom values
									if ( fieldObject.fieldType == 'select' ) {
										// Check if it supports custom values and it also supports tokens
										if ( isDefined( item.meta[ `${ fieldObject.optionCode }_custom` ] ) ) {
											// Add custom value
											fieldObject.customValue = item.meta[ `${ fieldObject.optionCode }_custom` ];
										}
									}

									// Check if it an AJAX field
									if ( fieldObject.isAjax ) {
										fieldObject.integrationId = globalItem.integration;

										// Check if it's an ajax field without target field
										// This means no other field will change when this one changes
										// and that we have to do an AJAX request when rendering the field, on page load
										if ( isEmpty( fieldObject.targetField ) ) {
											fieldObject.targetField = field.option_code;
										}
									}

									// Return field object
									return fieldObject;
								};

								let fieldObject, fieldInstance;

								if ( field.input_type == 'repeater' ) {
									// Try to parse the value of the repeater
									try {
										item.meta[ field.option_code ] = JSON.parse(
											item.meta[ field.option_code ]
										);
									} catch ( e ) {}

									// Get field data
									fieldObject = Object.assign( sanitizeFieldAttributes( field ), {
										groupId: groupKey,

										isGroup: hasMoreThanOneOption,
										itemId: item.ID,
										itemType: item.itemType,

										currentValue: isDefined( item.meta[ field.option_code ] )
											? item.meta[ field.option_code ]
											: field.default_value,

										dynamic_visibility: field.dynamic_visibility,
									} );

									// Create field instance
									fieldInstance = new FieldsRepeater( fieldObject );
								} else {
									// Otherwise, treat it as a normal field

									// Get the field data
									fieldObject = sanitizeFieldData( field );

									// Create the field instance
									fieldInstance = new Field( fieldObject.fieldType, fieldObject, false );
								}

								// Add field to the group of fields
								allFieldsOnThisGroup.push( fieldInstance );
							} );

							// Array where we're going to save the buttons of this item
							const buttons = [];

							// Check if this group of options has buttons
							if ( isDefined( globalItem.buttons ) ) {
								// Iterate buttons
								$.each( globalItem.buttons, ( i, button ) => {
									// Check if we have to add the button to this group
									if ( button.show_in == groupKey ) {
										buttons.push( button );
									}
								} );
							}

							// Push fields to the groupsOfOptions object
							groupsOfOptions[ groupKey ] = {
								fields: allFieldsOnThisGroup,
								isValid: false, // FALSE by default, but we're going to do a validation later
								buttons: buttons,
							};
						} );

						// Create object with the item data
						itemToAdd = {
							id: parseInt( item.ID ),
							itemType: item.itemType,
							itemCode: itemCode,
							integrationCode: globalItem.integration,
							isLive: item.post_status == 'publish' ? true : false,
							options: groupsOfOptions,
							meta: item.meta,
							isMissing: false,
							tokens: isDefined( item.tokens ) ? item.tokens : [],
							inlineCss: isDefined( globalItem.inline_css ) ? globalItem.inline_css : '',
							customHTML: isDefined( globalItem.custom_html ) ? globalItem.custom_html : '',
							// filterTokens: isDefined( globalItem.filter_tokens ) ? globalItem.filter_tokens : '',
							menuOrder: item.menu_order ? parseInt( item.menu_order ) : 9999,
						};

						// Check if the support link is defined
						if ( isDefined( globalItem.support ) ) {
							itemToAdd.support = globalItem.support;
						}

						// Create meta for those field that don't have a value saved in the database but have a default value

						// Get all fields
						const fields = {};

						// Create an object with all the options from the field
						$.each( groupsOfOptions, ( optionKey, opts ) => {
							$.each( opts.fields, ( i, field ) => {
								fields[ field.getAttributes().optionCode ] = field;
							} );
						} );

						$.each( fields, ( i, field ) => {
							const fieldAttributes = field.getAttributes();
							if (
								( ! isDefined( itemToAdd.meta[ fieldAttributes.optionCode ] ) ||
									isEmpty( itemToAdd.meta[ fieldAttributes.optionCode ] ) ) &&
								! isEmpty( fieldAttributes.currentValue )
							) {
								itemToAdd.meta[ fieldAttributes.optionCode ] = fieldAttributes.currentValue;
							}
						} );

						// If it's anonymous, add the "canLogUserIn" property
						// Also set the default value
						if ( recipeIsAnonymous ) {
							itemToAdd.canLogNewUserIn = isDefined( globalItem.can_log_in_new_user )
								? globalItem.can_log_in_new_user
								: true;
						}
					} else {
						// Create object with the item data
						itemToAdd = {
							id: parseInt( item.ID ),
							itemType: item.itemType,
							itemCode: item.meta.code,
							integrationCode: item.meta.integration,
							integrationName: item.meta.integration_name,
							sentence: item.meta.sentence,
							isLive: item.post_status == 'publish' ? true : false,
							tokens: [],
							options: {},
							meta: item.meta,
							isMissing: true,
							menuOrder: item.menu_order ? parseInt( item.menu_order ) : 9999,
						};

						// If it's anonymous, add the "canLogUserIn" property
						// Also set the default value
						if ( recipeIsAnonymous ) {
							itemToAdd.canLogNewUserIn = isDefined( item.meta.can_log_in_new_user )
								? item.meta.can_log_in_new_user == 'true'
								: true;
						}
					}

					// We have to add it
					itemsObject( 'add', itemToAdd );
				} );

				// Check if we should show the item ID
				// We make this outside of the loop because we need the data of all the items first
				flagItemsWithVisibleID();

				// Parse conditions in the recipe meta
				parseConditionsInMeta( recipe ); 

				// Flag scheduled actions and actions with conditions
				flagActionsWithConditions();
				flagActionsScheduled();
				flagActionsSortable();

				// Check if the recipe is anonymous and can log the new user in
				if ( recipeIsAnonymous ) {
					// Set the default value
					UncannyAutomator.recipe.canLogNewUserIn = false;

					// Get the recipe items
					const recipeTriggers = itemsObject( 'get', {
						filter: {
							itemType: 'trigger',
						},
					} );

					// Check if it has items
					if ( Object.keys( recipeTriggers ) > 0 ) {
						// Change the default value
						UncannyAutomator.recipe.canLogNewUserIn = true;

						// Iterate the triggers
						$.each( recipeTriggers, ( index, trigger ) => {
							// Check if this trigger allows the recipe
							// to log the new user in
							UncannyAutomator.recipe.canLogNewUserIn =
								UncannyAutomator.recipe.canLogNewUserIn && trigger.canLogNewUserIn;
						} );
					}
				}

				// Check if we should validate the data
				if (
					! isDefined( attributes.validate ) ||
					( isDefined( attributes.validate ) && attributes.validate )
				) {
					// Validate the recipe to update the isValid elements
					__deprecated__recipeObject( 'validate' );
				}

				break;

			case 'validate':
				// Valid by default, will be changed to false if we find an error
				let isRecipeValid = true;

				// Check if the recipe has triggers, actions and/or closures
				// Update UncannyAutomator.recipe.hasLive
				$.each( UncannyAutomator.recipe.hasLive, ( itemType ) => {
					// All false
					UncannyAutomator.recipe.hasLive[ itemType ] = false;
				} );

				// Check if there is at least one item of that type in the main object
				$.each( UncannyAutomator.recipe.items, ( itemID, itemData ) => {
					// If at least one live item, then set TRUE
					if ( itemData.isLive ) {
						UncannyAutomator.recipe.hasLive[ itemData.itemType ] = true;
					}
				} );

				// Iterate each item
				$.each( UncannyAutomator.recipe.items, ( itemID, itemData ) => {
					// We will use this variable to decide if the item is valid or not
					let itemIsValid = true;

					// Iterate each group of fields
					$.each( itemData.options, ( optionsGroupID, optionsGroupData ) => {
						// We will use this variable to decide if the group of fields is valid or not
						let groupIsValid = true;

						// Iterate each field
						$.each( optionsGroupData.fields, ( index, field ) => {
							// Validate the field. Read validateField comments to learn more.
							const fieldValidation = field.validate( false );

							// Check if the option is invalid, if at least one field is invalid make all the group invalid
							if ( ! fieldValidation.isValid ) {
								groupIsValid = false;
							}
						} );

						// Set result
						UncannyAutomator.recipe.items[ itemID ].options[ optionsGroupID ].isValid = groupIsValid;

						// Check if the group is invalid, if at least on group of fields is invalid make all the item invalid
						if ( ! groupIsValid ) {
							itemIsValid = false;
						}
					} );

					UncannyAutomator.recipe.items[ itemID ].isValid = itemIsValid;

					// Check if the item is invalid. If at least one item is invalid make all the recipe invalid
					if ( itemData.isLive && ! itemIsValid ) {
						isRecipeValid = false;
					}
				} );

				// Now check what items we have in the object
				// We must have at least one trigger and an action or closure
				// Check if it has at least one trigger
				if ( ! UncannyAutomator.recipe.hasLive.trigger ) {
					isRecipeValid = false;

					// Set error message
					UncannyAutomator.recipe.message.error = escapeHTML( __( 'You have to add at least one live trigger to your recipe', 'uncanny-automator' ) );
				} else {
					// Validate user selector
					// eslint-disable-next-line no-lonely-if
					if (
						// Check if it's an anonymous recipe
						UncannyAutomator._recipe.recipe_type == 'anonymous' &&
						// if it requires user data (thus it needs the user selector)
						UncannyAutomator.recipe.requiresUserData &&
						// and if the user selector is not valid (doesn't have required data)
						! UncannyAutomator.recipe.userSelector.isValid
					) {
						// Then the recipe is not valid
						isRecipeValid = false;

						// Add message
						UncannyAutomator.recipe.message.error = escapeHTML( __( 'Specify the user the actions will be run on', 'uncanny-automator' ) );
					} else if (
						// Check if it has at least one action or closure
						! UncannyAutomator.recipe.hasLive.action &&
						! UncannyAutomator.recipe.hasLive.closure
					) {
						isRecipeValid = false;

						// Set error message
						UncannyAutomator.recipe.message.error = escapeHTML( __( 'You have to add at least one live action to your recipe', 'uncanny-automator' ) );
					} else if (
						! UncannyAutomator.recipe.hasLive.trigger &&
						! UncannyAutomator.recipe.hasLive.action &&
						! UncannyAutomator.recipe.hasLive.closure
					) {
						// Set error message
						UncannyAutomator.recipe.message.error = escapeHTML( __( 'Add live items to your recipe', 'uncanny-automator' ) );
					}
				}

				UncannyAutomator.recipe.isValid = isRecipeValid;

				// Reset message if recipe is valid
				if ( isRecipeValid ) {
					// Reset message, we don't need it
					UncannyAutomator.recipe.message.error = '';
				}

				// Everything is ok, return true
				response = true;

				break;
		}
	} else if ( UncannyAutomator._site.has_debug_enabled ) {
		// eslint-disable-next-line no-console
		console.error( `__deprecated__recipeObject: The action "${ action }" isn't defined` );
	}

	return response;
};

/**
 * Add, update, delete, update, validate and clean the main recipe object
 *
 * @param {String}  action              Action to perform. Valid actions: 'add', 'update', 'delete', 'reset', 'get', and 'clean'.
 * @param {Object}  attributes          The attributes
 * @param {Integer} attributes.id       Item ID
 * @param {String}  attributes.itemType The item type. The valid types are 'trigger', 'action' and 'closure'
 * @return {Boolean}                       True if the action was made successfully
 */
export const itemsObject = ( action = null, attributes = {} ) => {
	// Create response variable and set default value
	let response = false;

	// Check if the action is not undefined and if it's a valid action
	switch ( action ) {
		case 'add':
			// Extend default attributes object with default values
			attributes = $.extend(
				{
					isValid: false, // Because it's going to be empty
					isLive: false, // Draft by default
				},
				attributes
			);

			// Has ID, check if it wasn't added
			if ( ! isDefined( UncannyAutomator.recipe.items[ attributes.id ] ) ) {
				// Create property
				UncannyAutomator.recipe.items[ attributes.id ] = attributes;

				// Clean undefined properties
				itemsObject( 'clean', {} );

				// Everything is ok, return true
				response = true;
			}

			break;

		case 'delete':
			attributes = $.extend(
				{
					isValid: false,
				},
				attributes
			);

			if ( ! isEmpty( attributes.id ) ) {
				if ( isDefined( UncannyAutomator.recipe.items[ attributes.id ] ) ) {
					delete UncannyAutomator.recipe.items[ attributes.id ];

					// Everything is ok, return true
					response = true;
				} else if ( UncannyAutomator._site.has_debug_enabled ) {
					// eslint-disable-next-line no-console
					console.error( `itemsObject: We didn't find an item with this the ID ${ attributes.id }` );
				}
			} else if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.error( 'itemsObject: Invalid item ID' );
			}

			break;

		case 'clean':
			// Delete undefined and null properties

			const items = {};

			$.each( UncannyAutomator.recipe.items, ( index, value ) => {
				if ( isDefined( value ) ) {
					items[ index ] = value;
				}
			} );

			UncannyAutomator.recipe.items = items;

			// Everything is ok, return true
			response = true;

			break;

		case 'update':
			// Update item attributes

			if ( ! isEmpty( attributes.id ) ) {
				if ( isDefined( UncannyAutomator.recipe.items[ attributes.id ] ) ) {
					// Extend item object properties with new ones
					UncannyAutomator.recipe.items[ attributes.id ] = $.extend(
						{},
						UncannyAutomator.recipe.items[ attributes.id ],
						attributes
					);

					// Everything is ok, return true
					response = true;
				} else if ( UncannyAutomator._site.has_debug_enabled ) {
					// eslint-disable-next-line no-console
					console.error( `itemsObject: We didn't find an item with this the ID ${ attributes.id }` );
				}
			} else if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.error( 'itemsObject: Invalid item ID' );
			}

			break;

		case 'reset':
			// Delete all the items in the items object
			UncannyAutomator.recipe.items = {};

			// Return true
			response = true;
			break;

		case 'get':
			response = UncannyAutomator.recipe.items;

			// Check if we should include only some items
			if ( isDefined( attributes.includeOnly ) && attributes.includeOnly.length === 0 ) {
				// Check if we should return an array
				if ( ! isEmpty( attributes.format ) && attributes.format == 'array' ) {
					return [];
				}

				return {};
			}

			if ( ! isEmpty( attributes.includeOnly ) ) {
				// Create a new items object. We're going to add items to this one if the filters match
				// eslint-disable-next-line no-shadow
				const items = {};

				// Iterate all current items
				Object.entries( response ).forEach( ( [ itemId, item ] ) => {
					// Check if it's one of the items we should add
					if ( attributes.includeOnly.includes( parseInt( itemId ) ) ) {
						items[ itemId ] = item;
					}
				} );

				// Return the new items object
				response = items;
			}

			// Check if we should exclude some items
			if ( ! isEmpty( attributes.exclude ) ) {
				// Create a new items object. We're going to add items to this one if the filters match
				// eslint-disable-next-line no-shadow
				const items = {};

				// Iterate all current items
				Object.entries( response ).forEach( ( [ itemId, item ] ) => {
					// Check if it's one of the items we should add
					if ( ! attributes.exclude.includes( parseInt( itemId ) ) ) {
						items[ itemId ] = item;
					}
				} );

				// Return the new items object
				response = items;
			}

			// Check if we have to filter items
			if ( ! isEmpty( attributes.filter ) ) {
				// Create a new items object. We're going to add items to this one if the filters match
				// eslint-disable-next-line no-shadow
				const items = {};

				// Iterate all current items
				Object.entries( response ).forEach( ( [ itemId, item ] ) => {
					// Default response
					let matchesFilters = true;

					// Iterate filters
					Object.entries( attributes.filter ).forEach( ( [ key, value ] ) => {
						// Only return the entries that match with the filters used
						if ( item[ key ] != value ) {
							matchesFilters = false;
						}
					} );
					
					// Check if the item matches the filters
					if ( matchesFilters ) {
						items[ itemId ] = item;
					}
				} );

				// Return the new items object
				response = items;
			}

			// Check if we should return an array
			if ( ! isEmpty( attributes.format ) && attributes.format == 'array' ) {
				response = Object.values( response );
			}

			// Check if we have to order the items. If so, also check if the we're requesting an array
			// We can't rely on the JS object
			if ( ! isEmpty( attributes.orderBy ) && ( ! isEmpty( attributes.format ) && attributes.format == 'array' ) ) {					
				// Check if we should use a custom function
				if ( typeof attributes.orderBy === 'function' ) {
					try {
						// Sort it. This will already be an array
						response = response.sort( attributes.orderBy );
					} catch ( e ) {
						if ( UncannyAutomator._site.has_debug_enabled ) {
							// eslint-disable-next-line no-console
							console.error( `itemsObject: The compare function \`orderBy\` thrown an error` );
							// eslint-disable-next-line no-console
							console.error( e );
							// eslint-disable-next-line no-console
							console.log( attributes.orderBy );
						}
					}
				} else {
					// Create the default compare function
					const compare = ( a, b ) => {
						// Check if both values are equal, and compare by ID
						// Otherwise, continue
						if ( a[ attributes.orderBy ] === b[ attributes.orderBy ] ) {
							if ( isDefined( a.id ) && isDefined( b.id ) ) {
								return a.id < b.id;
							}
						}

						// Check if the property is defined
						if ( 
							! isDefined( a[ attributes.orderBy ] ) || 
							! isDefined( b[ attributes.orderBy ] ) 
						) {
							return 0;
						}

						// Values to compare
						let valA = a[ attributes.orderBy ], 
							valB = b[ attributes.orderBy ];

						// Check if the property is numeric
						if ( isDefined( attributes.orderByType ) ) {
							if ( attributes.orderByType == 'int' ) {
								valA = parseInt( valA );
								valB = parseInt( valB );
							}
						}

						return valA > valB ? 1 : -1;
					} 

					// Sort it. This will already be an array
					response = response.sort( compare );
				}
			}

			break;
	}

	return response;
};

/**
 * Add the triggers and actions to each integration.
 * Change the data structure so it's possible to know
 * what items and integrations are pro
 *
 * @return {undefined}
 */
export const groupItemsByIntegration = () => {
	// Iterate the integrations
	// eslint-disable-next-line no-unused-vars
	Object.entries( UncannyAutomator.integrations ).forEach( ( [ integrationID, integration ] ) => {
		// Get the list of available triggers and actions
		// Sort them alphabetically
		const triggers = {
				available: sortArrayOfObjectsAlphabetically(
					UncannyAutomator.triggers.filter( ( item ) => item.integration == integrationID ),
					'select_option_name',
					( sentence ) => replaceCurlyBraces( sentence )
				),
				unavailable: [],
			},
			actions = {
				available: sortArrayOfObjectsAlphabetically(
					UncannyAutomator.actions.filter( ( item ) => item.integration == integrationID ),
					'select_option_name',
					( sentence ) => replaceCurlyBraces( sentence )
				),
				unavailable: [],
			};

		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			// Check if there are PRO items for this integration
			if (
				isDefined( UncannyAutomator.pro_items ) &&
				isDefined( UncannyAutomator.pro_items[ integrationID ] )
			) {
				// Get the recipe type
				let recipeType = UncannyAutomator._recipe.recipe_type;
				// Just for filtering the triggers, change "user" to "logged-in"
				recipeType = recipeType == 'user' ? 'logged-in' : recipeType;

				// Push the pro triggers to the unavailable triggers array
				// Sort them alphabetically
				triggers.unavailable = sortArrayOfObjectsAlphabetically(
					// Elements to sort alphabetically
					UncannyAutomator.pro_items[ integrationID ].triggers
						// Filter by recipe type
						// trigger.type is the trigger time
						.filter( ( trigger ) => trigger.type == recipeType ),
					// Sort them by name
					'name',
					// Remove the curly braces before comparing the names
					( sentence ) => replaceCurlyBraces( sentence )
				);

				// Push the pro actions to the unavailable actions array
				// Sort them alphabetically
				actions.unavailable = sortArrayOfObjectsAlphabetically(
					UncannyAutomator.pro_items[ integrationID ].actions,
					'name',
					( sentence ) => replaceCurlyBraces( sentence )
				);
			}
		}

		// Check if the integration has available triggers and actions
		triggers.hasAvailableItems = triggers.available.length > 0;
		actions.hasAvailableItems = actions.available.length > 0;

		// Check if the integration has unavailable triggers and actions
		triggers.hasUnavailableItems = triggers.unavailable.length > 0;
		actions.hasUnavailableItems = actions.unavailable.length > 0;

		/**
		 * Checks if all available items in a collection have a specific property set to true.
		 * 
		 * @param {Object} collection - The collection of items to evaluate.
		 * @param {String} property - The property name to check on each item.
		 * @returns {Boolean} - Returns true if all items have the specified property set to true, otherwise false.
		 */
		const checkAllItemsHaveProperty = (collection, property) => 
			collection.hasAvailableItems 
				? collection.available.every( item => 
					isDefined( item[ property ] ) && item[ property ]
				) 
				: false;

		// Evaluate if all the available items in the collection are pro
		triggers.allItemsArePro = checkAllItemsHaveProperty( triggers, 'is_pro' );
		actions.allItemsArePro = checkAllItemsHaveProperty( actions, 'is_pro' );

		// Evaluate if all the available items in the collection are elite
		triggers.allItemsAreElite = checkAllItemsHaveProperty( triggers, 'is_elite' );
		actions.allItemsAreElite = checkAllItemsHaveProperty( actions, 'is_elite' );

		// Check if it has any kind of items
		triggers.hasTriggers = triggers.hasAvailableItems || triggers.unavailable.length > 0;
		actions.hasActions = actions.hasAvailableItems || actions.unavailable.length > 0;

		// Add the items to the integration
		UncannyAutomator.integrations[ integrationID ].items = {
			triggers: triggers,
			actions: actions,
		};

		// Add conditions
		addConditionsToIntegrations( {
			integrationCode: integrationID
		} );
	} );
};

/**
 * Sorts an array of objects using one of the properties
 *
 * @param {Array}    items             The array
 * @param {String}   propertyToCompare The property used to compare the elements
 * @param {Function} filter            A custom function to compare the elements
 * @return {Array}                       The array, sorted
 */
export const sortArrayOfObjectsAlphabetically = ( items = [], propertyToCompare = '', filter ) => {
	// Check if there isn't a filter defined
	if ( ! isDefined( filter ) ) {
		filter = ( value ) => value;
	}

	// Check if it's an array and it's not empty
	if ( Array.isArray( items ) && ! isEmpty( items ) ) {
		// Sort the array of integrations using the integration name
		items = items.sort( ( a, b ) => {
			// Define the default value
			let compare = 0;

			// Check if the elements have the properties we're going to use to compare
			if ( isDefined( a[ propertyToCompare ] ) && isDefined( b[ propertyToCompare ] ) ) {
				// Filter the values
				const valueA = filter( a[ propertyToCompare ] ),
					valueB = filter( b[ propertyToCompare ] );

				// Check which integration name should go first
				compare = valueA.localeCompare( valueB );

				// Validate the result
				// Do NOT rely on exact return values of -1 or 1
				// Negative and positive integer results vary between browsers (as well as between browser versions) because the W3C specification only mandates negative and positive values. Some browsers may return -2 or 2, or even some other negative or positive value.
				compare = compare > 0 ? 1 : compare;
				compare = compare < 0 ? -1 : compare;
			}

			// Return result
			return compare;
		} );
	}

	// Return the new array
	return items;
};

/**
 * Returns a list of triggers or actions by integration
 *
 * @param {String} integrationID The integration ID
 * @param {String} itemType      The item type. Valid values: "trigger", "triggers", "action" and "actions"
 * @param {Object} options       The options
 * @return {Object}                And object with the available and unavailable items
 */
export const getItemsByIntegration = ( integrationID = '', itemType = 'triggers', options ) => {
	// Extend the options
	options = Object.assign(
		{
			addDeprecatedItems: false,
		},
		options
	);

	// Create the array where we're going to save the items
	let items = {
		available: {},
		unavailable: {},
	};

	// Convert itemType "trigger" to "triggers"
	itemType = itemType == 'trigger' ? 'triggers' : itemType;
	// Convert itemType "action" to "actions"
	itemType = itemType == 'action' ? 'actions' : itemType;

	// Validate the parameter itemType
	if ( [ 'triggers', 'actions' ].includes( itemType ) ) {
		// Check if the integration ID is defined
		if ( ! isEmpty( integrationID ) ) {
			// Check if the integration exists
			if ( isDefined( UncannyAutomator.integrations[ integrationID ] ) ) {
				// Get the requested items
				items = $.extend( true, {}, UncannyAutomator.integrations[ integrationID ].items[ itemType ] );

				// Check if we have to remove the deprecated items
				if ( ! options.addDeprecatedItems ) {
					// Remove deprecated items
					items = filterDeprecatedItems( items );
				}
			}
		}
	}

	// Return the triggers
	return items;
};

/**
 * Removes deprecated items from an items object
 *
 * @param {Object} items Items object
 * @return {Object}        Items object, without deprecated items
 */
export const filterDeprecatedItems = ( items ) => {
	// Remove deprecated items
	items.available = items.available.filter( ( item ) => ! item.is_deprecated );

	// Check if there are still available items
	items.hasAvailableItems = items.available.length > 0;

	// Check if it still has triggers or actions
	if ( isDefined( items.hasTriggers ) ) {
		items.hasTriggers = items.hasAvailableItems || items.unavailable.length > 0;
	} else if ( isDefined( items.hasActions ) ) {
		items.hasActions = items.hasAvailableItems || items.unavailable.length > 0;
	}

	// Check if all the available items are pro now
	items.allItemsArePro = items.available.every( ( item ) => ( isDefined( item.is_pro ) ? item.is_pro : false ) );

	return items;
};

/**
 * Updates the user selector data
 *
 * @param {String} action     Action to perform. Valid actions: 'set-step', 'get-step', 'set-source', 'update-fields', 'reset-fields'.
 * @param {Object} attributes The attributes
 * @return {Boolean}                       True if the action was made successfully
 */
export const userSelectorObject = ( action = null, attributes = {} ) => {
	// Create response variable and set default value
	let response = false;

	// Define valid actions
	const validActions = [ 'set-step', 'get-step', 'set-source', 'update-fields', 'reset-fields' ];

	// Create shortcut of the user selector object
	// const userSelector = UncannyAutomator.recipe.userSelector;

	// Check if the action is not undefined and if it's a valid action
	if ( isDefined( action ) && inArray( action, validActions ) ) {
		// Create empty array to check if the actions have the required data
		let requiredData = [];

		switch ( action ) {
			/**
			 * Get the current step so we know what we have to render
			 * There are 4 steps:
			 *
			 * 1. select-source: Where the recipe creator select the source of the user.
			 *                   The options are "Existing user" and "New user".
			 * 2. set-options: Where the recipe creator defines values of the fields.
			 * 3. show-sentence: When the data is complete and valid.
			 */

			case 'set-step':
				// Define required attributes properties
				requiredData = [ 'step' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					// Set status
					UncannyAutomator.recipe.userSelector.step = attributes.step;
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `userSelectorObject: Some attributes are missing` );
					}
				}

				break;

			case 'get-step':
				// Create the variable we're going to return
				// Set default value
				let step = 'select-source';

				// Check if the step property is defined
				if ( isDefined( UncannyAutomator.recipe.userSelector.step ) ) {
					// If it is, then return that value
					step = UncannyAutomator.recipe.userSelector.step;
				} else if (
					// Try to get step
					// Check if there are fields saved
					! isEmpty(
						UncannyAutomator.recipe.userSelector.data[ UncannyAutomator.recipe.userSelector.source ]
					)
				) {
					// Show sentence
					step = 'show-sentence';

					// If we have fields it's because we already valid them before,
					// thus the user selector is valid
					UncannyAutomator.recipe.userSelector.isValid = true;
				}

				// Return step
				response = step;

				break;

			case 'set-source':
				// Define required attributes properties
				requiredData = [ 'source' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					// Set status
					UncannyAutomator.recipe.userSelector.source = attributes.source;
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `userSelectorObject: Some attributes are missing` );
					}
				}

				break;

			case 'update-fields':
				// Define required attributes properties
				requiredData = [ 'source', 'data' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					// Check if the source parameter is valid
					if ( [ 'existingUser', 'newUser' ].includes( attributes.source ) ) {
						// Check if we already have fields
						if ( isEmpty( UncannyAutomator.recipe.userSelector.data ) ) {
							UncannyAutomator.recipe.userSelector.data = {
								existingUser: {},
								newUser: {},
							};
						}

						// Update fields
						UncannyAutomator.recipe.userSelector.data[ attributes.source ] = $.extend(
							{},
							UncannyAutomator.recipe.userSelector.data[ attributes.source ],
							attributes.data
						);
					} else if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( 'userSelectorObject: The source parameter is invalid. Valid values are: existingUser, newUser' );
					}
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `userSelectorObject: Some attributes are missing` );
					}
				}

				break;

			case 'reset-fields':
				// Return boolean
				UncannyAutomator.recipe.userSelector.data = {
					existingUser: {},
					newUser: {},
				};
				break;
		}
	} else if ( UncannyAutomator._site.has_debug_enabled ) {
		// eslint-disable-next-line no-console
		console.error( `userSelectorObject: The action "${ action }" isn't defined` );
	}

	return response;
};

/**
 * Filters triggers, actions and closures by recipe type.
 * This won't filter the items of a recipe, but the available
 * items in Automator. This means that the filtered items won't
 * be available after.
 *
 * @return {undefined}
 */
export const filterAutomatorItemsByRecipeType = () => {
	// Check if the type attribute has a valid value
	if ( ! [ 'user', 'anonymous' ].includes( UncannyAutomator._recipe.recipe_type ) ) {
		return;
	}

	// Filter triggers by checking if the trigger type matches the recipe type
	UncannyAutomator.triggers = UncannyAutomator.triggers.filter( 
		trigger => trigger.type == UncannyAutomator._recipe.recipe_type 
	);
};

/**
 * Performs a REST Call
 *
 * @param {String}   endPoint  Endpoint of the REST call
 * @param {Object}   data      Data to be sent in the call
 * @param {Function} onSuccess Function to be invoked if the call is successful
 * @param {Function} onFail    Function to be invoked if the call fails
 * @param {Object}   options   The parameters sent in the call
 * @return {undefined}
 */
export const restCall = ( endPoint = null, data = null, onSuccess = null, onFail = null, options = {} ) => {
	// Define the valid endpoints
	const validEndpoints = [
		'actions_conditions_fields',
		'actions_order_and_conditions',
		'actions_order',
		'change_post_status',
		'recipe_completions_allowed',
		'recipe_max_completions_allowed',
		'remove_schedule',
		'schedule_action',
		'set_recipe_requires_user',
		'set_recipe_terms',
		'update',
		'user-selector',
	];

	// Link to the support page
	const supportPageURL = UncannyAutomator._site.automator.links.contact_support;

	// Check custom options
	options = Object.assign({
		requiresRecipesObject: true,
		renderUI: true,
		errorMessaging: {
			// Access denied (access-denied)
			// 403. Usually caused by an invalid nonce or a server configuration.
			// 👉 Reloading the page might fix the issue.
			accessDenied: {
				title: escapeHTML( __( 'Access denied', 'uncanny-automator' ) ),
				content: sprintf(
					/* translators: 1. The endpoint */
					escapeHTML( __( 'Automator experienced a permissions (403) error. This might be caused by an expired WordPress session or a REST endpoint access issue. Automator specifically failed to do a REST call to %1$s. Reloading the page may fix the issue, otherwise have your host investigate why requests to %1$s are returning a 403 error.', 'uncanny-automator' ) ),
					`<strong>${ endPoint }</strong>`
				),
			},
			// Not found.
			// 404. Thrown when Automator tries to do a request to a non-existing endpoint.
			// 👉 Most probably an issue in Automator. Requires support.
			notFound: {
				title: sprintf( 
					/* translators: 1. The name of the endpoint */
					escapeHTML( __( 'Endpoint %1$s not found', 'uncanny-automator' ) ), 
					`<code style="margin: 0 6px">${ endPoint }</code>` 
				),
				content: sprintf(
					/* translators: 1. Link to "Site permalinks". 2. Link to "Uncanny Automator support" */
					escapeHTML( __( 'A endpoint that Automator requires is missing. If your %1$s are set to Plain, please change them to something else. Otherwise, removing and reinstalling Automator plugins to rule out an upload issue is recommended, otherwise please contact %2$s.', 'uncanny-automator' ) ), 
					`<a href="${ UncannyAutomator._site.links.wp_permalinks }" target="_blank">${ escapeHTML( __( 'site permalinks', 'uncanny-automator' ) ) }</a>`,
					`<a href="${ supportPageURL }" target="_blank">${ escapeHTML( __( 'Automator support', 'uncanny-automator' ) ) }</a>`
				)
			},
			// Request timeout. 
			// 408. Caused by a server without enough resources.
			// 👉 Requires the user to increase resources.
			timeout: {
				title: escapeHTML( __( 'Request timeout', 'uncanny-automator' ) ),
				content: escapeHTML( __( 'The Automator request timed out, likely due to insufficient server resources. Please contact your host.', 'uncanny-automator' ) ),
			},
			// Internal server error
			// 500. Server encountered an unexpected condition. We can't know if it's caused by Automator or another plugin automatically, we need to check the debug data shown below. Most of the time is an issue on Automator.
			// 👉 Requires support.
			serverError: {
				title: escapeHTML( __( 'Internal error', 'uncanny-automator' ) ),
				content: sprintf(
					/* translators: 1. Link to "Uncanny Automator support" */
					escapeHTML( __( 'Automator experienced a fatal error on your site. Please check your PHP and debug error log for more details, then sent the associated error details to %1$s.', 'uncanny-automator' ) ),
					`<a href="${ supportPageURL }" target="_blank">${ escapeHTML( __( 'Automator support', 'uncanny-automator' ) ) }</a>`
				),
			},
			// Parser error. 
			// There is likely a conflict with another plugin that throws a warning, making the returned data invalid.
			// 👉 Most of the time, disabling all plugins but Automator will be enough. It might also be an issue in Automator, so it might require support.
			parserError: {
				title: escapeHTML( __( 'Parser error', 'uncanny-automator' ) ),
				content: escapeHTML( __( "We have detected a conflict with another plugin. Try testing in a Staging environment with only Automator plugins active, then gradually reactivate plugins until things break again to trace what's causing it.", 'uncanny-automator' ) ),
			},
			// Data missing.
			// Caused by missing data in Automator.
			// 👉 Requires support.
			dataMissing: {
				title: escapeHTML( __( 'Data missing', 'uncanny-automator' ) ),
				content: sprintf(
					/* translators: 1. Link to contact support. 2. Link to the "Automator Tools" page */
					escapeHTML( __( 'Automator has detected that expected data is missing. Please %1$s with details of the issue and a copy of the System Report on the %2$s page.', 'uncanny-automator' ) ),
					`<a href="${ supportPageURL }" target="_blank">${ escapeHTML( __( 'contact support', 'uncanny-automator' ) ) }</a>`,
					`<a href="${ UncannyAutomator._site.automator.links.tools }" target="_blank">${ escapeHTML( __( 'Automator Tools', 'uncanny-automator' ) ) }</a>`
				)
			},
			// Generic error
			// Fallback
			generic: {
				title: escapeHTML( __( 'Unknown error', 'uncanny-automator' ) ),
				content: sprintf(
					/* translators: 1. Link to contact support */
					escapeHTML( __( 'Automator has encountered an unknown error. Please contact %1$s with a screenshot of your recipe, details about what you were doing and any other information that may help us with the error.', 'uncanny-automator' ) ),
					`<a href="${ supportPageURL }" target="_blank">${ escapeHTML( __( 'Automator support', 'uncanny-automator' ) ) }</a>`
				),
			}
		}
	}, options );

	// Check if the endPoint parameter is a valid endpoint
	if ( inArray( endPoint, validEndpoints ) ) {
		// Do AJAX
		$.ajax( {
			method: 'POST',
			url: `${ UncannyAutomator._site.rest.url }/${ endPoint }/`,
			// eslint-disable-next-line camelcase
			data: `${ $.param( data ) }&${ $.param( { doing_rest: 1 } ) }`,
			dataType: 'json',

			/**
			 * Attachs Nonce the the header of the request
			 *
			 * @param {Object} xhr jqXHR
			 * @return {undefined}
			 */
			beforeSend: ( xhr ) => {
				xhr.setRequestHeader( 'X-WP-Nonce', UncannyAutomator._site.rest.nonce );
			}
		} )
			// eslint-disable-next-line require-jsdoc
			.done( ( response, textStatus, errorThrown ) => {
				// Check if the request succeeded
				if ( response.success ) {
					// If new recipe data is available, update it
					if ( ! isEmpty( response._recipe ) ) {
						UncannyAutomator._recipe = JSON.parse( response._recipe );
					}

					// Check if we have the new recipes_object, otherwise the data will change on the server but not in the UI
					if ( isDefined( response.recipes_object ) || ! options.requiresRecipesObject ) {
						// Check if onSuccess
						if ( isDefined( onSuccess ) ) {
							// Check if we have to render the UI again
							if ( options.requiresRecipesObject && options.renderUI ) {
								__deprecated__recipeObject( 'create', {
									allRecipes: response.recipes_object,
								} );
							}

							// Invoke callback
							onSuccess( response );
						}
					} else {
						// The call was successful, but the new recipes_object is missing
						if ( UncannyAutomator._site.has_debug_enabled ) {
							// eslint-disable-next-line no-console
							console.error( 'The call was successful, but the new recipes_object is missing.' );
						}

						// Check if the onFail callback is defined
						if ( isDefined( onFail ) ) {
							// Invoke callback
							onFail( response, 'data-missing' );
						}
					}
				} else {
					// Check if the onFail callback is defined
					if ( isDefined( onFail ) ) {
						// Invoke callback
						onFail( response, 'data-missing' );
					}
				}
			} )
			// eslint-disable-next-line require-jsdoc
			.fail( ( jqXHR, textStatus, errorThrown ) => {
				// Get error code
				let errorCode = textStatus;

				// Get error messaging
				let errorMessaging = options.errorMessaging.generic;

				// Improve readability of the error
				switch ( errorCode ){
					case 'parsererror':
						// Set code
						errorCode = 'parser-error';

						// Set messaging
						errorMessaging = options.errorMessaging.parserError;

						break;

					case 'error':
						// Check the code
						if ( jqXHR.status == 403 ){
							// Set code
							errorCode = 'access-denied';

							// Set messaging
							errorMessaging = options.errorMessaging.accessDenied;
						} else if ( jqXHR.status == 404 ){
							// Set code
							errorCode = 'not-found';

							// Set messaging
							errorMessaging = options.errorMessaging.notFound;
						} else if ( jqXHR.status == 500 ){
							// Set code
							errorCode = 'server-error';

							// Set messaging
							errorMessaging = options.errorMessaging.serverError;
						}

						break;

					case 'timeout':
						// Set code
						errorCode = 'timeout';

						// Set messaging
						errorMessaging = options.errorMessaging.timeOut;

						break;
				}

				if ( isDefined( onFail ) ) {
					onFail( {
						success: false
					}, errorCode );
				}

				// Handle error by showing a modal with data
				handleFatalError({
					title:   errorMessaging.title,
					warning: errorMessaging.content,
					content: '',
					console: `Data sent:\n` +
						`${ JSON.stringify( data ) }\n\n` +
						`errorCode: \n${ errorCode }\n\n` +
						`errorThrown:\n${ JSON.stringify( errorThrown ) }\n\n` +
						`jqXHR: \n${ JSON.stringify( jqXHR ) } \n\n`,
					buttons: {
						confirm: null,
						cancel: {
							label: escapeHTML( _x( 'Close', 'Verb', 'uncanny-automator' ) ),
							onClick: ( $modal ) => {
								// Destroy modal
								$modal.destroy();
							},
						},
					}
				});
			} );
	} else if ( UncannyAutomator._site.has_debug_enabled ) {
		// eslint-disable-next-line no-console
		console.error( `The ${ endPoint } endPoint does not exists. The valid endpoints are ${ validEndpoints.join( ', ' ) }` );
	}
};

/**
 * Performs an AJAX request
 *
 * @param {String}   action    The ID of the action
 * @param {Object}   data      Data to be sent in the request
 * @param {Function} onSuccess Function to be invoked if the request is successful
 * @param {Function} onFail    Function to be invoked if the request fails
 * @return {undefined}
 */
export const AJAXRequest = ( action = null, data = null, onSuccess = null, onFail = null ) => {
	// Check that {action} is defined
	if ( isDefined( action ) ) {
		// Add {action} to the data object
		data = $.extend( true, data, {
			action: action,
			nonce: UncannyAutomator._site.rest.nonce,
		} );

		// Do AJAX
		$.ajax( {
			method: 'POST',
			dataType: 'json',
			url: ajaxurl,
			data: data,

			// eslint-disable-next-line require-jsdoc
			success: ( response ) => {
				// Reload page if user authentication fails
				if ( isDefined( response.status ) && response.status == 'auth-failed' ) {
						
					// Check if the onFail callback is defined
					if ( isDefined( onFail ) ) {
						// Invoke callback
						onFail( response );
					}

					// Handle error by showing a modal with data
					handleFatalError({
						title:   escapeHTML( __( 'Access denied', 'uncanny-automator' ) ),
						warning: sprintf(
							/* translators: 1. The endpoint */
							escapeHTML( __( 'Automator experienced a permissions (403) error. This might be caused by an expired WordPress session or a REST endpoint access issue. Automator specifically failed to do a REST call to %1$s. Reloading the page may fix the issue, otherwise have your host investigate why requests to %1$s are returning a 403 error.', 'uncanny-automator' ) ),
							`<strong>${ action }</strong>`
						),
						content: '',
						console: `Data sent:\n` +
							`${ JSON.stringify( data ) }\n\n` +
							`errorCode: \naccess-denied\n\n`,
						buttons: {
							confirm: null,
							cancel: {
								label: escapeHTML( _x( 'Close', 'Verb', 'uncanny-automator' ) ),
								onClick: ( $modal ) => {
									// Destroy modal
									$modal.destroy();
								},
							},
						}
					});

				} else if ( isDefined( onSuccess ) ) {
					// Invoke callback
					onSuccess( response, data );
				}
			},

			statusCode: {
				// eslint-disable-next-line require-jsdoc
				403: ( response ) => {
					// Check if the onFail callback is defined
					if ( isDefined( onFail ) ) {
						// Invoke callback
						onFail( response );
					}

					// Handle error by showing a modal with data
					handleFatalError({
						title:   escapeHTML( __( 'Access denied', 'uncanny-automator' ) ),
						warning: sprintf(
							/* translators: 1. The endpoint */
							escapeHTML( __( 'Automator experienced a permissions (403) error. This might be caused by an expired WordPress session or a REST endpoint access issue. Automator specifically failed to do a REST call to %1$s. Reloading the page may fix the issue, otherwise have your host investigate why requests to %1$s are returning a 403 error.', 'uncanny-automator' ) ),
							`<strong>${ action }</strong>`
						),
						content: '',
						console: `Data sent:\n` +
							`${ JSON.stringify( data ) }\n\n` +
							`errorCode: \naccess-denied\n\n`,
						buttons: {
							confirm: null,
							cancel: {
								label: escapeHTML( _x( 'Close', 'Verb', 'uncanny-automator' ) ),
								onClick: ( $modal ) => {
									// Destroy modal
									$modal.destroy();
								},
							},
						}
					});
				},
			},

			// eslint-disable-next-line require-jsdoc
			fail: ( response ) => {
				if ( isDefined( onFail ) ) {
					onFail( response );
				}
			},
		} );
	} else if ( UncannyAutomator._site.has_debug_enabled ) {
		// eslint-disable-next-line no-console
		console.error( 'We cannot do this AJAX request. {action} is not defined.' );
	}
};

// Idempotent registration — utilities.js may be evaluated multiple times in
// the legacy bundle. The guard lives on `window` so it actually survives
// re-evaluation (a module-scoped `let` would re-initialize to false on each
// evaluation, defeating the guard).
if (
	typeof window !== 'undefined'
	&& ! window.__uapRemoteDataNonceRegistered
	&& typeof UncannyAutomator !== 'undefined'
	&& UncannyAutomator?._site?.rest?.nonce
) {
	apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomator._site.rest.nonce ) );
	window.__uapRemoteDataNonceRegistered = true;
}

/**
 * REST equivalent of {@link AJAXRequest}. Same callback contract — call sites only differ
 * in which function they invoke. Request and response shapes are identical to the legacy
 * admin-ajax handler, so callers do not branch on transport beyond the function choice.
 *
 * @param {{id: string, data: string}} remoteData - Field's `remote_data` attribute (id + data segments)
 * @param {Object}                     payload    - Same payload AJAXRequest receives
 * @param {Function}                   onSuccess  - Invoked with (response, payload) on resolve
 * @param {Function}                   onFail     - Invoked with (response) on reject
 * @return {undefined}
 */
export const RESTOptionsRequest = ( remoteData = null, payload = null, onSuccess = null, onFail = null ) => {
	if ( ! isDefined( remoteData ) || ! isDefined( remoteData?.id ) || ! isDefined( remoteData?.data ) ) {
		if ( SITE_CONFIG.DEV.SHOW_CONSOLE_MESSAGES ) {
			// eslint-disable-next-line no-console
			console.error( 'RESTOptionsRequest: remote_data.id or remote_data.data is not defined.' );
		}
		return;
	}

	const url = `${ UncannyAutomator._site.rest.url }/remote-data/${ remoteData.id }/${ remoteData.data }`;

	apiFetch( { url, method: 'POST', data: payload } )
		.then( ( response ) => {
			if ( isDefined( onSuccess ) ) {
				onSuccess( response, payload );
			}
		} )
		.catch( ( response ) => {
			if ( isDefined( onFail ) ) {
				onFail( response );
			}
		} );
};

/**
 * Apply remote_data defaults to a field's attributes object.
 *
 * PHP opts a field into the REST path by emitting a single `remote_data` block
 * — `{ id, data, event, listen_fields }`. This helper fans that single block
 * out into the three slots the rest of the field engine reads from
 * (`remote_type === 'default'`, `remote_data: { id, data }`,
 * `ajax: { event, listenFields, ... }`) so downstream code stays byte-identical
 * to the legacy admin-ajax path.
 *
 * Mutates `attributes` in place — same convention as the surrounding
 * `setAttributes` calls. Always sets `remote_type` (defaults to `'legacy'`)
 * so downstream `=== 'default'` checks are unambiguous.
 *
 * @param {Object} attributes - Field attributes (mutated in place).
 * @return {undefined}
 */
export const applyRemoteDataDefaults = ( attributes ) => {
	if (
		attributes.remote_data &&
		'object' === typeof attributes.remote_data &&
		attributes.remote_data.data
	) {
		attributes.remote_type = 'default';
		// Fan the single PHP-emitted block out into the field engine's
		// camelCase ajax slots. Each key is event-conditional on the PHP side
		// (event => listen_fields for parent_fields_change, event =>
		// clear_on_change_fields for search_options, mapping_column for
		// repeaters) so we copy each one only when present.
		attributes.ajax = Object.assign(
			{},
			attributes.ajax || {},
			attributes.remote_data.event ? { event: attributes.remote_data.event } : {},
			Array.isArray( attributes.remote_data.listen_fields )
				? { listenFields: attributes.remote_data.listen_fields }
				: {},
			Array.isArray( attributes.remote_data.clear_on_change_fields )
				? { clearOnChangeFields: attributes.remote_data.clear_on_change_fields }
				: {},
			attributes.remote_data.mapping_column
				? { mappingColumn: attributes.remote_data.mapping_column }
				: {}
		);
	}
	attributes.remote_type = attributes.remote_type || 'legacy';
};

/**
 * This function is used to get the unsaved values of the fields.
 * It handles exceptions for loop filters and for actions, triggers, and closures.
 * It iterates over each field and gets its value.
 * If the field is a select with a custom value, it gets the custom value.
 * It also gets the readable value if the field is a select and its value is not empty.
 * 
 * @param {Object} params - The parameters for the function.
 * @param {String} params.itemType - The type of the item. e.g. 'trigger', 'action', 'loop-filter'
 * @param {String} params.itemID - The ID of the item.
 * @param {(Field|undefined)} [params.field] - The field instance.
 * @return {Object} values - The unsaved values of the fields.
 */
export const getUnsavedFieldsValues = ( { itemType, itemID, field } ) => {
	const values = {};

	/**
	 * Exception for loop filters
	 * @todo Re-do once fields become web components
	 */
	if ( itemType === 'loop-filter' ) {
		// Check if the item was already created
		if ( isDefined( itemID ) ) {
			// Get the form
			const $form = document.querySelector( `uap-builder-item-legacy-form[item-id="${ itemID }"]` );

			if ( $form ) {
				return $form.unsavedFieldValues;
			}
		} else {
			// Get the form from the dialog (the item is being created, so no ID is defined)
			const $form = document.querySelector( `uap-builder-item-loop-filter-edit-dialog uap-builder-item-legacy-form:not([item-id])` );

			if ( $form ) {
				return $form.unsavedFieldValues;
			}
		}
	}

	/**
	 * Exception for conditions
	 */
	if ( itemType === 'condition' ) {
		// Check if the item was already created
		if ( isDefined( itemID ) ) {
			// Get the form
			const $form = document.querySelector( `.uap-conditions-options-fields uap-builder-item-legacy-form[item-id="${ itemID }"]` );

			if ( $form ) {
				return $form.unsavedFieldValues;
			}
		} else {
			// Get the form from the dialog (the item is being created, so no ID is defined)
			const $form = document.querySelector( `.uap-conditions-options-fields uap-builder-item-legacy-form:not([item-id])` );

			if ( $form ) {
				return $form.unsavedFieldValues;
			}
		}
	}

	/**
	 * Exception for actions, triggers and closures
	 * @todo Re-do once fields become web components
	 */
	if ( [ 'action', 'trigger', 'closure' ].includes( itemType ) ) {
		// Check if the itemID is defined. This means that the item was added to the database
		// If it's not defined, it means the item is being created and the fields are not saved yet
		if ( isDefined( itemID ) ) {
			$.each( UncannyAutomator.recipe.items[ itemID ].options, ( index, groupOfFields ) => {
				// Iterate each field
				$.each( groupOfFields.fields, ( i, field ) => {
					// Get value
					const fieldId = field.getFieldID();
					let fieldValue = field.getValue();

					// Check if it isn't defined. In that case get the current value from the currentValue attribute
					if ( ! isDefined( fieldValue ) ) {
						fieldValue = field.getAttributes().currentValue;
					}

					// Check if the field is a select with custom value
					if ( field.getAttributes().fieldType == 'select' && isCustomValue( fieldValue ) ) {
						values[ `${ fieldId }_custom` ] = field.getSelectCustomValue();
					}

					// Save value
					values[ fieldId ] = fieldValue;

					// Check if it's a select and also get the readable value
					if ( ! isEmpty( fieldValue ) && field.getAttributes().fieldType == 'select' ) {
						// Get and save the readable value
						values[ `${ fieldId }_readable` ] = field.getReadableValue();
					}
				} );
			} );

			return values;
		}

		if ( isDefined( field ) ) {
			// Try to get it using the field data
			try {
				field.$wrapper.get(0).parentNode.querySelectorAll( 'select, input, textarea' ).forEach( ( formField ) => {
					if ( isDefined( formField.fieldInstance ) ) {
						values[ formField.fieldInstance.attributes.optionCode ] = formField.fieldInstance.getValue();
					}
				} );
			} catch ( e ) {
				console.log( e );
			}

			return values;
		}
	}

	return values;
}

/**
 * Returns a Field instance given a item and field ID
 *
 * @param {Integer} itemId  The item ID
 * @param {String}  fieldId The field ID
 * @param {String}  itemType The item type
 * @return {Field}            The Field instance
 */
export const getField = ( itemId, fieldId, itemType ) => {
	let field;

	if ( [ 'loop-filter', 'publish-setting', 'condition' ].includes( itemType ) ) {
		// Check if the item was already created
		if ( isDefined( itemId ) ) {
			// Get the form
			const $form = document.querySelector( `uap-builder-item-legacy-form[item-id="${ itemId }"]` );

			if ( $form && isDefined( $form.fields[ fieldId ] ) ) {
				// Get the field
				return $form.fields[ fieldId ];
			}
		} else {
			// The selector base for the form
			let formSelectorBase = 'uap-builder-item-loop-filter-edit-dialog';
			if ( itemType === 'publish-setting' ) {
				formSelectorBase = 'uap-builder-publish-settings-edit-dialog';
			}
			if ( itemType === 'condition' ) {
				formSelectorBase = '.uap-conditions-options';
			}

			// Get the form from the dialog (the item is being created, so no ID is defined)
			const $form = document.querySelector( `${ formSelectorBase } uap-builder-item-legacy-form:not([item-id])` );

			if ( $form && isDefined( $form.fields[ fieldId ] ) ) {
				// Get the field
				return $form.fields[ fieldId ];
			}
		}

		return;
	}

	// Make sure the data required exists
	if ( ! isEmpty( itemId ) && ! isEmpty( fieldId ) && isDefined( UncannyAutomator.recipe.items[ itemId ] ) ) {
		$.each( UncannyAutomator.recipe.items[ itemId ].options, ( optionsGroupId, optionsGroup ) => {
			$.each( optionsGroup.fields, ( index, fieldInstance ) => {

				if ( fieldId == fieldInstance.getFieldID() ) {
					field = fieldInstance;
				}
			} );
		} );
	} else {
		// Work around for conditions
		// This MUST change once we extend the <uo-text-field> web component and use it here
		// AND we change how AJAX is used on fields
		const $fieldFromDOM = document.querySelector( `[name="${ fieldId }"]:not([data-item-id])` );

		if ( isDefined( $fieldFromDOM ) && isDefined( $fieldFromDOM.fieldInstance ) ) {
			field = $fieldFromDOM.fieldInstance;
		}
	}

	return field;
};

/**
 * Returns data about a trigger, action or closure
 *
 * @param {Integer} integrationID The item ID
 * @param {String}  itemType      The item type. Valid values are trigger, action and closure
 * @param {String}  itemCode      The item code
 * @return {Object}                Item's data
 */
export const getGlobalItem = ( integrationID = '', itemType = '', itemCode = '' ) => {
	let theItem;

	// Check if all are defined
	if ( ! isEmpty( integrationID ) && ! isEmpty( itemType ) && ! isEmpty( itemCode ) ) {
		let integrationItems = {};

		// Check if the we're trying to get a trigger or action
		if ( itemType == 'trigger' || itemType == 'action' ) {
			// Check if the integration exists
			if ( isDefined( UncannyAutomator.integrations[ integrationID ] ) ) {
				// Create a reference to the integration
				const integration = UncannyAutomator.integrations[ integrationID ];

				// Check if the items are defined
				if ( isDefined( integration.items ) ) {
					// Set integrationItems
					integrationItems =
						itemType == 'trigger'
							? integration.items.triggers.available
							: integration.items.actions.available;
				}
			}
		} else if ( itemType == 'closure' ) {
			integrationItems = UncannyAutomator.closures;
		}

		// Search for the item
		$.each( integrationItems, ( index, item ) => {
			if ( item.code == itemCode ) {
				// Save the item
				theItem = item;

				// Return false in the loop callback to break the loop
				return false;
			}
		} );
	}

	// Return the item
	return theItem;
};

/**
 * Sets the recipe "requires user" value
 *
 * @param {Boolean}  requires  TRUE if the recipe requires a user
 * @param {Function} onSuccess Callback, on success
 * @param {Function} onFail    Callback, on fail
 * @return {undefined}
 */
export const recipeSetRequiresUser = ( requires = false, onSuccess, onFail ) => {
	restCall(
		'set_recipe_requires_user',
		{
			recipePostID: UncannyAutomator._recipe.recipe_id,
			requiresUser: requires,
		},
		( response ) => {
			// Check if the call was successful
			if ( response.success ) {
				// Check if the onSuccess callback is defined
				if ( isDefined( onSuccess ) ) {
					// Update the global "requiresData"
					UncannyAutomator.recipe.requiresUserData = requires;

					// Get the User Selector
					const $userSelector = getDOMElements( 'user-selector' );
					// Show it
					$userSelector.addClass( 'user-selector--show' );

					onSuccess( response );
				}
			}
		},
		( response ) => {
			if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.log( "Couldn't set the recipe requires user status" );
			}

			// Check if the onFail callback is defined
			if ( isDefined( onFail ) ) {
				onFail( response );
			}
		},
		{
			requiresRecipesObject: false
		}
	);
};

/**
 * Returns DOM elements, such as a metabox, item of optionGroup
 * In Automator DOM elements are deleted and created in each step, so the reference to a DOM element change.
 *
 * @param {String} element    Code of the element that you want to get
 * @param {Object} attributes Contains required data to get the elements. Depends of the "element" value. Check the variable requiredData in each case.
 * @return {Object}             Object with DOM elements. The properties depend of "element"
 */
export const getDOMElements = ( element, attributes = {} ) => {
	// Create response object
	let response = {};

	// Define valid actions
	const validElements = [ 'metabox', 'items', 'item', 'optionGroup', 'lastItem', 'user-selector', 'itemsContainer' ];

	// Check if the action is not undefined and if it's a valid action
	if ( isDefined( element ) && inArray( element, validElements ) ) {
		// Create array that will store all the requires attributes properties based on the requested element
		let requiredData = [];

		switch ( element ) {
			case 'metabox':
				// Extend default attributes object with default values
				attributes = $.extend( {}, attributes );

				// Define required attributes properties
				requiredData = [ 'type' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					attributes.cssSelectorMetabox = '';

					// Check the itemType
					if ( attributes.type == 'trigger' ) {
						attributes.cssSelectorMetabox = '#recipe-triggers-ui';
					} else if ( attributes.type == 'action' ) {
						attributes.cssSelectorMetabox = '#recipe-actions-ui';
					}

					const $metabox = $( attributes.cssSelectorMetabox );

					response = $metabox;
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `itemsObject: Some attributes are missing` );
					}
				}

				break;

			case 'items':
				// Extend default attributes object with default values
				attributes = $.extend( {}, attributes );

				// Define required attributes properties
				requiredData = [ 'type' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					const metabox = getDOMElements( 'metabox', {
						type: attributes.type,
					} );

					// Get jQuery DOM element
					const $items = metabox.find(
						'.uap-item:not(.item--user-selector):not(.uap-item--actions-initial):not(.item--closure)'
					);

					// Add element to the response
					response = $items;
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `itemsObject: Some attributes are missing` );
					}
				}

				break;

			case 'item':
				// Extend default attributes object with default values
				attributes = $.extend( {}, attributes );

				// Define required attributes properties
				requiredData = [ 'id' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					// Define CSS Selector
					attributes.cssSelector = `.uap .uap-item[data-id="${ attributes.id }"]`;

					// Get jQuery DOM element
					const $item = $( attributes.cssSelector ),
						$contentContainer = $item.find( '.uap-item-inner-content' );

					// Add element to the response
					response.item = $item;
					response.contentContainer = $contentContainer;
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `itemsObject: Some attributes are missing` );
					}
				}

				break;

			case 'optionGroup':
				// Extend default attributes object with default values
				attributes = $.extend( {}, attributes );

				// Define required attributes properties
				requiredData = [ 'itemID', 'groupID' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					// Get main object
					const item = getDOMElements( 'item', { id: attributes.itemID } ).item;

					// Define CSS Selector
					attributes.cssSelectorTokenInSentence = `.item-title__token[data-token-id="${ attributes.groupID }"]`;
					attributes.cssSelectorFieldsContainer = `.item-options__group[data-optiongroup="${ attributes.groupID }"]`;

					const $tokenInSentence = item.find( attributes.cssSelectorTokenInSentence ),
						$fieldsContainer = item.find( attributes.cssSelectorFieldsContainer ),
						$optionsContainer = item.find( '.item-options' );

					response.tokenInSentence = $tokenInSentence;
					response.fieldsContainer = $fieldsContainer;
					response.optionsContainer = $optionsContainer;
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `itemsObject: Some attributes are missing` );
					}
				}

				break;

			case 'lastItem':
				// Extend default attributes object with default values
				attributes = $.extend( {}, attributes );

				// Define required attributes properties
				requiredData = [ 'type' ];

				// Check if the attributes object has all the required properties
				if ( inArray( requiredData, Object.keys( attributes ) ) ) {
					// Get metabox
					const metabox = getDOMElements( 'metabox', {
						type: attributes.type,
					} );

					// Get last item
					const lastItem = metabox
						.find( '.uap-item:not(.item--user-selector):not(.uap-item--actions-initial):not(.item--closure)' )
						.last();

					response = lastItem;
				} else {
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.error( `itemsObject: Some attributes are missing` );
					}
				}

				break;

			case 'user-selector':
				// Get user selector container
				response = $( '.user-selector' );

				break;
		
			case 'itemsContainer':
				
				if ( ! isEmpty( attributes.type ) ) {
					if ( attributes.type === 'trigger' ) {
						response = document.querySelector( '#recipe-triggers-ui .uap-items' );
					} else if ( attributes.type === 'action' ) {
						if ( ! isEmpty( attributes.actionContext ) ) {
							switch ( attributes.actionContext ) {
								case 'instant':
									response = document.querySelector( '#recipe-actions-ui .uap-items.uap-items--actions-instant' );
									break;

								case 'delayed':
									response = document.querySelector( '#recipe-actions-ui .uap-items.uap-items--actions-delayed' );
									break;

								case 'scheduled':
									response = document.querySelector( '#recipe-actions-ui .uap-items.uap-items--actions-scheduled' );
									break;
							}
						}
					}
				}

				break;
		}
	} else if ( UncannyAutomator._site.has_debug_enabled ) {
		// eslint-disable-next-line no-console
		console.error( `getDOMElements: The element "${ element }" isn't defined` );
	}

	return response;
};

/**
 * Parses string with SENTENCE tokens
 *
 * @param {Object} item   The item
 * @param {String} string The string with the tokens
 * @param {Object} data   Object with the data to replace the text in the tokens.
 * @return {Object}  response                   Object with information about the string
 *         {String}  response.html              The string with the tokens replaced with the data and enclosed in HTML tags
 *         {Object}  response.elements          Node parts of the sentence
 *         {Array}   response.elements.tokens   Array of jQuery object. All the tokens part of the sentence
 *         {Array}   response.parts             Object with information about each part of the sentence
 */
export const parseStringWithTokens = ( item = null, string = '', data = {} ) => {
	let response = null;

	// Create response object
	response = {
		plain: '',
		brackets: '',
		html: $( '<div/>' ),
		elements: {
			normal: [],
			token: [],
		},
		parts: [],
	};

	// Trim string
	string = typeof string === 'string' ? string.trim() : '';

	// Check if the string isn't empty
	if ( ! isEmpty( string ) ) {
		// Create regex
		const tokensRegex = new RegExp( /\{\{(.*?)\}\}/gm ),
			normals = [],
			tokens = [];

		let mappedString = string;

		// Get tokens
		let tokenNumber = 0;

		string = string.replace( tokensRegex, ( fullMatch, token ) => {
			const tokenParts = token.split( ':' );

			// Get the token placeholder text
			const tokenPlaceholderText = tokenParts[ 0 ];

			// Get the token value ID
			const tokenValueID = tokenParts[ 1 ];

			// Get the ID of the group of fields this token has to open
			// Check if the token should open a different group of options
			const optionsID = isDefined( tokenParts[ 2 ] ) ? tokenParts[ 2 ] : tokenValueID;

			token = {
				type: 'token',
				id: tokenValueID,
				text: tokenPlaceholderText,
				optionsID: optionsID,
				hasFields: isDefined( item.options[ optionsID ] ),
			};

			// Check if we have data for the token
			if ( isDefined( data[ token.id ] ) ) {
				let readableText;
				// Check if the value is a custom value and if it has a readable value
				if ( 
					isCustomValue( data[ token.id ] ) && 
					isDefined( data[ `${ token.id }_custom` ] ) 
				) {
					readableText = data[ `${ token.id }_custom` ];
				} else if ( isDefined( data[ `${ token.id }_readable` ] ) ) {
					readableText = data[ `${ token.id }_readable` ];
				}

				token.originalText = token.text;
				token.text = isDefined( readableText ) ? readableText : data[ token.id ];
			}

			// Add token
			tokens.push( token );

			// Add token to the mapped string
			mappedString = mappedString.replace( fullMatch, `{{token:${ tokenNumber }}}` );
			tokenNumber++;

			return '{{v}}';
		} );

		const stringParts = string.split( '{{v}}' );

		// Get normals
		let normalNumber = 0;
		$.each( stringParts, ( key, sentenceSection ) => {
			if ( sentenceSection != '' ) {
				// Create normal part
				const normal = {
					type: 'normal',
					text: sentenceSection,
				};

				// Add normal text
				normals.push( normal );

				// Add "normal" part to the mapped string
				mappedString = mappedString.replace( sentenceSection, `{{normal:${ normalNumber }}}` );
				normalNumber++;
			}
		} );

		// Create response.parts
		let match;

		while ( ( match = tokensRegex.exec( mappedString ) ) !== null ) {
			// Get info
			let part = match[ 1 ].split( ':' );

			part = {
				type: part[ 0 ],
				index: part[ 1 ],
			};

			// Get part object and add to parts
			if ( part.type == 'normal' ) {
				response.parts.push( normals[ part.index ] );
			} else {
				response.parts.push( tokens[ part.index ] );
			}
		}

		// Check if the sentence has tokens
		let itemTokens = null;

		// Create plain text and HTML versions
		$.each( response.parts, ( index, part ) => {
			// Plain text
			response.plain += part.text;

			// HTML
			if ( part.text != ' ' ) {
				let elementAttributes,
					text = part.text;

				let contentIsNode = false,
					tagNodeContent = null;

				if ( part.type == 'normal' || ( part.type == 'token' && ! part.hasFields && ! item.isMissing ) ) {
					elementAttributes = {
						class: 'item-title__normal',
					};

					// Add normal-text part to the brackets version
					response.brackets += part.text;
				} else {
					const CSSClass = 'item-title__token';

					elementAttributes = {
						class: CSSClass,
						'data-token-id': part.id,
						'data-options-id': part.optionsID,
					};

					// Check if it's using a valid ID
					if ( isDefined( item.options[ part.optionsID ] ) ) {
						// Check if the section is valid
						if ( item.options[ part.optionsID ].isValid ) {
							// Add filled class
							elementAttributes.class += ' item-title__token--filled';
						}
					}

					// Check if it's filled
					if ( isDefined( part.originalText ) ) {
						// Add filled class
						// elementAttributes[ 'class' ] += ' item-title__token--filled';

						// Check if the text is a token
						// let isValidToken = false;

						// Remove HTML from the text. We'll only add plain text in the sentence
						const $temp = document.createElement( 'span' );
						$temp.innerText = text;
						text = $temp.innerText;

						if ( ( new RegExp( '\{\{(.*?)\}\}', 'gi' ) ).test( text ) ) {
							// Check if we already have the tokens data
							if ( ! isDefined( itemTokens ) ) {
								itemTokens = new Tokens( item.itemType, 'text', {
									includeGroupName: false,
									itemID: item.id,
									forceShowBothLoopableAndNonLoopable: true
								} );
							}

							// Text attributes
							const textAttributes = {}

							// Get first and last three words
							const words = text.split( /\s+/ );
							if ( words.length > 6 ) {
								text = `${ words.slice( 0, 3 ).join( ' ' ) } ... ${ words.slice( -3 ).join( ' ' ) }`;
							}

							// Check if there is an ID defined
							if ( isDefined( item ) && isDefined( item.id ) ) {
								textAttributes.itemID = item.id;
							}

							// Check if it's a valid token
							const token = itemTokens.renderTextWithTokens( text, textAttributes );

							if ( isDefined( token ) ) {
								// Change the validity of the token
								// isValidToken = true;
								contentIsNode = true;
								tagNodeContent = token;
							}
						}

						if ( ! contentIsNode ) {
							// Define text of the token
							const abbreviatedText = abbreviateText( text );

							// Check if the text was long enough to abbreviate it
							if ( abbreviatedText.isAbbreviated ) {
								// Add tooltip with the complete text
								elementAttributes[ 'uap-tooltip' ] = text;

								// Replace original text
								text = abbreviatedText.text;
							}
						}

							// Try to add the name of the label
							// First make sure the item is not missing
							if ( ! item.isMissing ) {
								try {
									// Get the label of the field attached to this part of the sentence
									const linkedField = getField( item.id, part.id, item.itemType );
									const fieldLabel = linkedField.attributes.label;

									// Check if it's just text for now
									if ( ! contentIsNode ) {
										const tempText = `${ linkedField.attributes.showLabelInSentence ? `<span class="item-title__token-label">${ fieldLabel }:</span>` : '' } ${ isEmpty( text ) ? `<em class="uap-empty-value">${ escapeHTML( __( '(empty)', 'uncanny-automator' ) ) }</em>` : text }`;
										text = tempText.trim();
								} else {
									const $wrap = document.createElement( 'span' );
									$wrap.appendChild( tagNodeContent );

									if ( linkedField.attributes.showLabelInSentence ) {
										$wrap.insertAdjacentHTML( 'afterBegin', `<span class="item-title__token-label">${ fieldLabel }: </span>` );
									}

									tagNodeContent = $wrap;
								}
							} catch ( e ) {}
						}
					}

					// Add token to the brackets version
					response.brackets += `{{${ decode( text ) }}}`;
				}

				const element = $( '<span/>', elementAttributes );

				if ( contentIsNode ) {
					element.append( tagNodeContent );
				} else {
					element.html( text );
				}					

				// Add to the elements array
				response.elements[ part.type ].push( element );

				// Append HTML
				response.html.append( element );
			} else {
				// Add white spaces to the brackets version
				response.brackets += part.text;
			}
		} );

		// Convert array of jQuery elements into wrapped set of elements
		response.elements.normal = $( response.elements.normal ).map( ( index, element ) => {
			return element.get();
		} );
		response.elements.token = $( response.elements.token )
			.map( ( index, element ) => {
				return element.get();
			} )
			.filter( ':not(.item-title__normal)' );
	}

	return response;
};

/**
 * Checks if a value exists in an array
 * If the needle is an array then it will check if all his elements exists in the haystack
 *
 * @param {Mixed} needle   The searched value. The comparison between values is strict. If the needle is an array then it will check if all his elements exists in the haystack
 * @param {Array} haystack An array through which to search.
 * @return {Boolean}           True if needle is found in the array or if needle is an array, TRUE if all the elements from the needle are in haystack
 */
export const inArray = ( needle, haystack ) => {
	if ( Array.isArray( needle ) ) {
		return needle.every( v => haystack.includes( v ) );
	}

	// Check if one element is in an array
	return haystack.includes( needle );
};

/**
 * Determines whether a variable is a string
 *
 * @param {Mixed} string The variable being evaluated
 * @return {Boolean}         True if the variable is an string
 */
export const isString = ( string ) => {
	return typeof string === 'string';
};

/**
 * Replaces curly braces
 *
 * @param {String} string The string to be searched
 * @param {String} start  The value to replace the for {{
 * @param {String} end    The value to replace the for }}
 * @return {String}          The value with the replaced values
 */
export const replaceCurlyBraces = ( string = '', start = '', end = '' ) => {
	string = string.replace( /{{/g, start );
	string = string.replace( /}}/g, end );
	return string;
};

/**
 * Creates and show a modal with useful data when the JS throws a fatal error
 *
 * @param {Object} settings The settings
 * @return {undefined}
 */
export const handleFatalError = ( settings = {} ) => {
	settings = $.extend(
		true,
		{
			title: escapeHTML( __( 'Sorry, something went wrong', 'uncanny-automator' ) ),
			warning: '',
			content: escapeHTML( __( 'Click "Learn more" for steps you can take to resolve this issue.', 'uncanny-automator' ) ),
			console: null,
			buttons: {
				confirm: {
					label: escapeHTML( __( 'Learn more', 'uncanny-automator' ) ),
					onClick: () => {
						// Learn more
						window.open( UncannyAutomator._site.automator.links.debugging_guide, '_blank' );
					},
				},
				cancel: {
					label: escapeHTML( _x( 'Close', 'Verb', 'uncanny-automator' ) ),
					onClick: ( $modal ) => {
						// Destroy modal
						$modal.destroy();
					},
				},
			},
		},
		settings
	);

	// Create report log
	let reportLog = '';

	let recipeData;
	try {
		// Get the recipe data
		$.each( UncannyAutomator.recipes_object, ( index, theRecipe ) => {
			if ( theRecipe.ID == UncannyAutomator._recipe.recipe_id ) {
				recipeData = theRecipe;
			}
		} );
	} catch ( e ) {
		// Continue regardless of error

		// Output error if the developer mode is enabled
		if ( UncannyAutomator._site.has_debug_enabled ) {
			// eslint-disable-next-line no-console
			console.warn( e );
		}
	}

	// Get general information about the recipe
	try {
		let logRecipe = `====== Recipe ====== \n`;

		// Add recipe type
		let recipeType = 'User';
		if ( recipeData.recipe_type == 'anonymous' ) {
			recipeType = 'Everyone';
		}

		logRecipe += `Type: ${ recipeType }`;

		// Add recipe status
		let recipeStatus = 'Draft';
		if ( recipeData.post_status == 'publish' ) {
			recipeStatus = 'Live';
		}

		logRecipe += `\nStatus: ${ recipeStatus }`;

		reportLog = logRecipe;
	} catch ( e ) {
		// Continue regardless of error

		// Output error if the developer mode is enabled
		if ( UncannyAutomator._site.has_debug_enabled ) {
			// eslint-disable-next-line no-console
			console.warn( e );
		}
	}

	// Get information about the recipe items
	try {
		let logRecipeItems = `=== Recipe items === \n`;

		/**
		 * Cleans extra data
		 *
		 * @param {Object} item The item
		 * @return {String}      Information about the item
		 */
		const cleanItemData = ( item ) => {
			// Get status
			const recipeStatus =
				item.post_status == 'publish'
					? 'Live'
					: 'Draft';

			return `> ${ item.meta.integration } - ${
				item.meta.code
			} - ${ recipeStatus }\n  (${ item.meta.sentence.replace(
				/\{\{|\:[a-zA-Z0-9]*\}\}/g,
				''
			) })\n  ${ JSON.stringify( item )
				.replaceAll( '<', '&lt;' )
				.replaceAll( '>', '&gt;' ) }\n`;
		};

		// Get triggers
		const triggers = recipeData.triggers.map( cleanItemData );
		const hasTriggers = triggers.length > 0;

		// Get actions
		const actions = recipeData.actions.map( cleanItemData );
		const hasActions = actions.length > 0;

		// Get closures
		const closures = recipeData.closures.map( cleanItemData );
		const hasClosures = closures.length > 0;

		// Check if it has recipe items
		if ( hasTriggers || hasActions || hasClosures ) {
			// Add triggers
			logRecipeItems += `Triggers\n\n`;

			// Check if it has triggers
			if ( hasTriggers ) {
				logRecipeItems += triggers.join( '\n' );
			} else {
				logRecipeItems += `> No triggers`;
			}

			// Add actions
			logRecipeItems += `\nActions\n\n`;

			// Check if it has actions
			if ( hasActions ) {
				logRecipeItems += actions.join( '\n' );
			} else {
				logRecipeItems += `> No actions`;
			}

			// Add closures
			logRecipeItems += `\nClosures\n\n`;

			// Check if it has closures
			if ( hasClosures ) {
				logRecipeItems += closures.join( '\n' );
			} else {
				logRecipeItems += `> No closures`;
			}
		} else {
			logRecipeItems += `Recipe without items`;
		}

		reportLog = reportLog !== '' ? `${ reportLog }\n\n${ logRecipeItems }` : reportLog;
	} catch ( e ) {
		// Continue regardless of error

		// Output error if the developer mode is enabled
		if ( UncannyAutomator._site.has_debug_enabled ) {
			// eslint-disable-next-line no-console
			console.warn( e );
		}
	}

	// Add JS console
	try {
		// Check if we have content to add
		if ( isDefined( settings.console ) ) {
			// Output only if the developer mode is enabled
			if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.error( settings.console );
			}

			let jsConsole = `====== JS console ====== \n`;

			jsConsole += settings.console.stack || settings.console;

			reportLog = reportLog !== '' ? `${ reportLog }\n\n${ jsConsole }` : reportLog;
		}
	} catch ( e ) {
		// Continue regardless of error

		// Output error if the developer mode is enabled
		if ( UncannyAutomator._site.has_debug_enabled ) {
			// eslint-disable-next-line no-console
			console.warn( e );
		}
	}

	// Escape HTML from report log
	try {
		reportLog = reportLog
						.replace( /&/g, '&amp;' )
						.replace( /</g, '&lt;' )
						.replace( />/g, '&gt;' )
						.replace( /"/g, '&quot;' )
						.replace( /'/g, '&#039;' );
	} catch ( e ){
		// Continue regardless of error

		// Output error if the developer mode is enabled
		if ( UncannyAutomator._site.has_debug_enabled ) {
			// eslint-disable-next-line no-console
			console.warn( e );
		}
	}

	// Create buttons
	const modalButtons = {};

	if ( isDefined( settings.buttons.cancel ) ){
		modalButtons.cancel = settings.buttons.cancel.label;
	}

	if ( isDefined( settings.buttons.confirm ) ){
		modalButtons.confirm = settings.buttons.confirm.label;
	}

	let $modal = new Modal(
		{
			title: `<span class="modal__title-icon"><uo-icon id="triangle-exclamation"></uo-icon></span> ${ settings.title }`,
			warning: `${ settings.warning }`,
			content: `
			<div>
				${ ! isEmpty( settings.content ) ? settings.content : '' }
				<div class="modal__content-code">
					<pre id="uap-debugging">${ reportLog }</pre>
				</div>
			</div>
		`,
			buttons: modalButtons,
		},
		{
			size: 'extra-large',
			warningFirst: true,
		}
	);

	$modal.setEvents( {
		onConfirm: () => {
			settings.buttons.confirm.onClick( $modal );
		},
		onCancel: () => {
			settings.buttons.cancel.onClick( $modal );
		},
		afterSetEvents: () => {
			// Get the element with the debugging
			const $debuggingConsole = $( '#uap-debugging' );

			// On click, copy the content
			$debuggingConsole.on( 'click', () => {
				// Select the content of the container
				// This is just to improve the UX
				const range = document.createRange();
				range.selectNodeContents( $debuggingConsole[ 0 ] );
				// eslint-disable-next-line @wordpress/no-global-get-selection
				window.getSelection().removeAllRanges();
				// eslint-disable-next-line @wordpress/no-global-get-selection
				window.getSelection().addRange( range );
			} );
		},
	} );
};

/**
 * Opens a link on a new tab
 *
 * @param {String} href The URL
 * @return {undefined}
 */
export const openInNewTab = ( href ) => {
	Object.assign( document.createElement( 'a' ), {
		target: '_blank',
		href: href,
	} ).click();
};

/**
 * Determines if a variable is a string, array, or JSON string that includes 'automator_custom_value'
 *
 * @param {mixed} variable - The variable being evaluated
 *
 * @return {Boolean} TRUE if the variable includes 'automator_custom_value'
 */
export const isCustomValue = ( variable ) => {
   const custom = 'automator_custom_value';

   // Check if the variable is exactly 'automator_custom_value'
   if ( variable === custom ) {
	   return true;
   }

   // Check if the variable is an array that includes 'automator_custom_value'
   if ( Array.isArray( variable ) && variable.includes( custom ) ) {
	   return true;
   }

   // Check if the variable is a string representation of an array that includes 'automator_custom_value'
   if ( typeof variable === 'string' ) {
	   try {
		   const parsedValue = JSON.parse( variable );
		   if ( Array.isArray( parsedValue ) && parsedValue.includes( custom ) ) {
			   return true;
		   }
	   } catch ( e ) {
		   // Ignore JSON parse errors
	   }
   }

   return false;
}